import React, { useMemo, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { EmojiData, SkinTone } from '@appTypes/emoji';
import { buildSearchIndex, buildFullSearchIndex } from '@utils/searchIndex';
import { EmojiCategory } from './EmojiCategory';

interface EmojiGridProps {
  data: EmojiData;
  searchQuery: string;
  onEmojiClick: (emoji: string, event: React.SyntheticEvent<HTMLElement>) => void;
  skinTone?: SkinTone;
}

export const EmojiGrid = React.memo(
  ({ data, searchQuery, onEmojiClick, skinTone = 'default' }: EmojiGridProps) => {
    const { t } = useTranslation();

    // Start with EN-only index, then async-load full multilingual index
    const enIndex = useMemo(() => buildSearchIndex(data), [data]);
    const [searchIndex, setSearchIndex] = useState<Map<string, string>>(enIndex);

    useEffect(() => {
      let cancelled = false;
      buildFullSearchIndex(data).then((fullIndex) => {
        if (!cancelled) setSearchIndex(fullIndex);
      });
      return () => {
        cancelled = true;
      };
    }, [data]);

    const filteredCategories = useMemo(() => {
      if (!searchQuery.trim()) {
        return data.categories;
      }

      const query = searchQuery.toLowerCase();
      const matches = (char: string) => (searchIndex.get(char) ?? '').includes(query);

      return data.categories
        .map((category) => {
          if (category.subgroups && category.subgroups.length > 0) {
            const filteredSubgroups = category.subgroups
              .map((subgroup) => ({
                ...subgroup,
                emojis: subgroup.emojis.filter((emoji) => matches(emoji.char)),
              }))
              .filter((subgroup) => subgroup.emojis.length > 0);

            return { ...category, subgroups: filteredSubgroups };
          } else if (category.emojis && category.emojis.length > 0) {
            return {
              ...category,
              emojis: category.emojis.filter((emoji) => matches(emoji.char)),
            };
          }

          return category;
        })
        .filter((category) => {
          if (category.subgroups && category.subgroups.length > 0) {
            return category.subgroups.some((subgroup) => subgroup.emojis.length > 0);
          }
          return category.emojis && category.emojis.length > 0;
        });
    }, [data.categories, searchQuery, searchIndex]);

    if (filteredCategories.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            {t('noResults')}
          </Typography>
        </Box>
      );
    }

    return (
      <Box className="emoji-grid">
        {filteredCategories.map((category, index) => (
          <EmojiCategory
            key={category.id || index}
            category={category}
            onEmojiClick={onEmojiClick}
            skinTone={skinTone}
            skinToneOptions={data.skinToneOptions}
          />
        ))}
      </Box>
    );
  }
);
EmojiGrid.displayName = 'EmojiGrid';
